import { ApiError } from "./types";

export interface CalendarResponse<T> {
  data: T;
  demoMode: boolean;
  correlationId: string | null;
}

export interface CalendarClientOptions {
  baseUrl: string;
  getToken?: () => string | null;
  onUnauthorized?: () => void;
}

export type ScheduleLifecycle = "DRAFT" | "ACTIVE" | "PAUSED" | "RETIRED";
export type SourceFidelity = "CURRENT" | "STALE" | "WITHDRAWN" | "UNBOUND";
export type DateResponseState = "SCHEDULED" | "ACKNOWLEDGED" | "WAIVED";
export type DateLifecycleState = "DUE" | "OVERDUE" | "ACKNOWLEDGED" | "WAIVED";
export type WorkItemActionClass = "governed" | "operational";
export type WorkItemState = "open";

export interface ObligationSchedule {
  id: string;
  organizationId: string;
  derivationMode: string;
  sourceBindingId: string | null;
  authorityBasis: string | null;
  offsetDays: number | null;
  explicitDueDate: string | null;
  accountableOwner: string;
  timeZone: string;
  schedulerCalendarId: string | null;
  noticeOffsetsDays: number[];
  consequence: string;
  lifecycleState: ScheduleLifecycle;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface ObligatedDate {
  id: string;
  organizationId: string;
  scheduleId: string;
  dueDate: string;
  resolvedDueAt: string;
  accountableOwner: string;
  sourceRevision: string | null;
  sourceFidelity: SourceFidelity;
  responseState: DateResponseState;
  lifecycleState: DateLifecycleState;
  createdAt: string;
  updatedAt: string;
}

export interface SourceBinding {
  id: string;
  organizationId: string;
  sourceObjectType: string;
  sourceObjectId: string;
  owningApplication: string;
  sourceDateField: string;
  sourceRevision: string;
  observedDateValue: string;
  observedAt: string;
  boundBy: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface SourceBindingRegistryEntry {
  sourceObjectType: string;
  owningApplication: string;
  authorityMode: string;
  dateField: string;
  bindableR1: boolean;
}

export interface ReflectedDate {
  id: string;
  organizationId: string;
  sourceBindingId: string;
  owningApplication: string;
  authoritativeDateId: string | null;
  authoritativeDateValue: string;
  authoritativeState: string | null;
  sourceRevision: string;
  observedAt: string;
  actionEndpoint: string | null;
  mirrorStatus: string;
  incompleteReason: string | null;
  sourceFidelity: SourceFidelity;
  nonAuthoritative: true;
}

export interface ContextBridgeRelated {
  application: string;
  objectType: "obligated_date";
  count: number | null;
  basis: "asserted";
  state?: "unavailable";
  dates?: ObligatedDate[];
}

export interface ContextBridgeResponse {
  subject: { type: string; id: string };
  accepted: boolean;
  related: ContextBridgeRelated[];
}

export interface WorkItemEnvelope {
  idempotencyKey: string;
  workItemType: string;
  organizationId: string;
  application: "calendar_ai";
  subject: { type: "obligated_date"; id: string };
  title: string;
  actionClass: WorkItemActionClass;
  route: string;
  resolver: string;
  requiredPermission: string;
  assignedTo: { principalType: "user" | "role"; principalId: string };
  dueAt: string;
  raisedAt: string;
  state: WorkItemState;
  sourceRevision: number;
}

export interface AuditChainEntry {
  id: string;
  organizationId: string;
  actorId: string;
  action: string;
  subjectId: string;
  createdAt: string;
  [key: string]: unknown;
}

export interface CreateSourceBindingInput {
  sourceObjectType: string;
  sourceObjectId: string;
  sourceDateField: string;
}

export interface CreateObligationScheduleInput {
  sourceBindingId?: string;
  authorityBasis?: string;
  derivationMode: string;
  offsetDays?: number;
  explicitDueDate?: string;
  accountableOwner: string;
  timeZone: string;
  schedulerCalendarId?: string;
  noticeOffsetsDays: number[];
  consequence: string;
}

async function parse(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try { return JSON.parse(text) as unknown; } catch { return null; }
}

function raise(status: number, data: unknown): never {
  const error = (data as { error?: { code?: string; message?: string; details?: Record<string, unknown> } } | null)?.error;
  throw new ApiError(status, error?.code ?? "error", error?.message ?? `Request failed (${status}).`, error?.details);
}

export class CalendarAiClient {
  private readonly baseUrl: string;
  private readonly getToken: () => string | null;
  private readonly onUnauthorized: (() => void) | undefined;

  constructor(options: CalendarClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/$/, "");
    this.getToken = options.getToken ?? (() => null);
    this.onUnauthorized = options.onUnauthorized;
  }

  private async request<T>(method: string, orgId: string, path: string, body?: unknown): Promise<CalendarResponse<T>> {
    const headers: Record<string, string> = { Accept: "application/json" };
    const token = this.getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    if (body !== undefined) headers["Content-Type"] = "application/json";
    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}/organizations/${orgId}/calendar-ai/${path.replace(/^\/+/, "")}`, {
        method, headers, ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
    } catch {
      throw new ApiError(0, "network_error", "Unable to reach the server. Check your connection.");
    }
    if (response.status === 401) this.onUnauthorized?.();
    const data = await parse(response);
    if (!response.ok) raise(response.status, data);
    return {
      data: data as T,
      demoMode: response.headers.get("x-calendar-ai-mode") === "demo",
      correlationId: response.headers.get("x-correlation-id"),
    };
  }

  contextBridge(orgId: string, subjectType: string, subjectId: string) {
    return this.request<ContextBridgeResponse>(
      "GET", orgId,
      `context-bridge/subjects/${encodeURIComponent(subjectType)}/${encodeURIComponent(subjectId)}`,
    );
  }
  workItems(orgId: string) {
    return this.request<{ workItems: WorkItemEnvelope[]; declarations: unknown[] }>("GET", orgId, "attention/work-items");
  }
  schedules(orgId: string) {
    return this.request<{ schedules: ObligationSchedule[] }>("GET", orgId, "obligation-schedules");
  }
  schedule(orgId: string, scheduleId: string) {
    return this.request<{ schedule: ObligationSchedule }>("GET", orgId, `obligation-schedules/${encodeURIComponent(scheduleId)}`);
  }
  createSchedule(orgId: string, input: CreateObligationScheduleInput) {
    return this.request<{ schedule: ObligationSchedule }>("POST", orgId, "obligation-schedules", input);
  }
  updateSchedule(orgId: string, scheduleId: string, input: Partial<CreateObligationScheduleInput>) {
    return this.request<{ schedule: ObligationSchedule }>(
      "PATCH", orgId, `obligation-schedules/${encodeURIComponent(scheduleId)}`, input,
    );
  }
  transitionSchedule(orgId: string, scheduleId: string, input: { newState: ScheduleLifecycle; reason: string }) {
    return this.request<{ schedule: ObligationSchedule }>(
      "POST", orgId, `obligation-schedules/${encodeURIComponent(scheduleId)}/transitions`, input,
    );
  }
  dates(orgId: string) {
    return this.request<{ dates: ObligatedDate[]; awaitingResponseCount: number }>("GET", orgId, "obligated-dates");
  }
  date(orgId: string, dateId: string) {
    return this.request<{ date: ObligatedDate }>("GET", orgId, `obligated-dates/${encodeURIComponent(dateId)}`);
  }
  assignDate(orgId: string, dateId: string, accountableOwner: string) {
    return this.request<null>("POST", orgId, `obligated-dates/${encodeURIComponent(dateId)}/assign`, { accountableOwner });
  }
  acknowledgeDate(orgId: string, dateId: string, evidenceDocumentId?: string) {
    return this.request<null>(
      "POST", orgId, `obligated-dates/${encodeURIComponent(dateId)}/acknowledge`,
      evidenceDocumentId ? { evidenceDocumentId } : {},
    );
  }
  waiveDate(orgId: string, dateId: string, rationale: string) {
    return this.request<null>("POST", orgId, `obligated-dates/${encodeURIComponent(dateId)}/waive`, { rationale });
  }
  sourceBindings(orgId: string) {
    return this.request<{ bindings: SourceBinding[] }>("GET", orgId, "source-bindings");
  }
  sourceBindingRegistry(orgId: string) {
    return this.request<{ registry: SourceBindingRegistryEntry[] }>("GET", orgId, "source-binding-registry");
  }
  createSourceBinding(orgId: string, input: CreateSourceBindingInput) {
    return this.request<{ binding: SourceBinding }>("POST", orgId, "source-bindings", input);
  }
  reconcileSourceBinding(orgId: string, bindingId: string) {
    return this.request<{ outcome: "UNCHANGED" | "REFRESHED" | "SHIFTED" | "WITHDRAWN" }>(
      "POST", orgId, `source-bindings/${encodeURIComponent(bindingId)}/reconcile`,
    );
  }
  reflectedDates(orgId: string) {
    return this.request<{ reflectedDates: ReflectedDate[] }>("GET", orgId, "reflected-dates");
  }
  auditChain(orgId: string) {
    return this.request<{ valid: boolean; entries: AuditChainEntry[] }>("GET", orgId, "audit-chain");
  }
}
